import React, { useMemo } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import AskMe from "./AskMe";
import { tickerItems, socials } from "../constants/data";

/* Soft gradient blob that lazily follows the cursor */
const CursorBlob = () => {
    const x = useMotionValue(-200);
    const y = useMotionValue(-200);
    const sx = useSpring(x, { stiffness: 40, damping: 15 });
    const sy = useSpring(y, { stiffness: 40, damping: 15 });

    return (
        <div
            className="pointer-events-auto absolute inset-0 overflow-hidden"
            onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                x.set(e.clientX - rect.left - 240);
                y.set(e.clientY - rect.top - 240);
            }}
        >
            <motion.div
                style={{ x: sx, y: sy }}
                className="pointer-events-none absolute h-[480px] w-[480px] rounded-full opacity-40 blur-3xl"
            >
                <div className="h-full w-full rounded-full bg-[radial-gradient(circle_at_30%_30%,var(--blob-a),var(--blob-b)_60%,transparent_75%)]" />
            </motion.div>
        </div>
    );
};

const Ticker = () => {
    const doubled = useMemo(() => [...tickerItems, ...tickerItems], []);
    return (
        <div className="relative overflow-hidden border-y border-line bg-elevated/60 py-3">
            <div className="animate-marquee flex w-max gap-8 whitespace-nowrap">
                {doubled.map((item, i) => (
                    <span
                        key={i}
                        className="flex items-center gap-8 font-mono text-xs uppercase tracking-widest text-muted"
                    >
                        {item}
                        <span className="text-pop">✺</span>
                    </span>
                ))}
            </div>
        </div>
    );
};

const Header = () => {
    return (
        <section
            id="top"
            className="relative flex min-h-screen flex-col justify-end overflow-hidden pt-28"
        >
            {/* Blueprint grid, very faint */}
            <div
                className="pointer-events-none absolute inset-0 opacity-60"
                style={{
                    backgroundImage:
                        "linear-gradient(var(--grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--grid-line) 1px, transparent 1px)",
                    backgroundSize: "72px 72px",
                    maskImage:
                        "radial-gradient(ellipse 85% 75% at 50% 25%, black 30%, transparent 100%)",
                    WebkitMaskImage:
                        "radial-gradient(ellipse 85% 75% at 50% 25%, black 30%, transparent 100%)",
                }}
            />
            <CursorBlob />

            <div className="pointer-events-none relative z-10 mx-auto w-full max-w-7xl flex-1 px-5 sm:px-8">
                <div className="grid h-full items-center gap-12 py-12 lg:grid-cols-[1.15fr_1fr]">
                    <div className="pointer-events-auto">
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-widest2 text-muted"
                        >
                            <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-accent" />
                            Senior Software Engineer — Applied AI
                        </motion.p>

                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="text-[13vw] font-medium leading-[0.95] tracking-tight text-fg sm:text-7xl lg:text-8xl"
                        >
                            Harshit
                            <br />
                            <span className="text-outline">Chaunal</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.25 }}
                            className="mt-8 max-w-xl text-lg leading-relaxed text-muted"
                        >
                            I build{" "}
                            <span className="font-serif italic text-fg">
                                production systems
                            </span>{" "}
                            and{" "}
                            <span className="font-serif italic text-accent">
                                AI products
                            </span>{" "}
                            that survive contact with real users. Prefer a briefing?
                            Ask the agent on the right.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.4 }}
                            className="mt-10 flex flex-wrap items-center gap-6"
                        >
                            <a
                                href="#work"
                                className="group rounded-full bg-fg px-7 py-3.5 font-mono text-sm uppercase tracking-widest text-base transition-colors hover:bg-accent"
                            >
                                View Work{" "}
                                <span className="inline-block transition-transform group-hover:translate-x-1">
                                    →
                                </span>
                            </a>
                            <a
                                href="#contact"
                                className="link-sweep pb-1 font-mono text-sm uppercase tracking-widest text-muted transition-colors hover:text-fg"
                            >
                                Say Hello
                            </a>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.7, delay: 0.55 }}
                            className="mt-10 flex gap-6 font-mono text-xs uppercase tracking-widest text-faint"
                        >
                            <a
                                href={socials.github}
                                target="_blank"
                                rel="noreferrer"
                                className="transition-colors hover:text-accent"
                            >
                                GitHub ↗
                            </a>
                            <a
                                href={socials.linkedin}
                                target="_blank"
                                rel="noreferrer"
                                className="transition-colors hover:text-accent"
                            >
                                LinkedIn ↗
                            </a>
                            <a
                                href={socials.resume}
                                download
                                className="transition-colors hover:text-accent"
                            >
                                Résumé ↓
                            </a>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.45 }}
                        className="pointer-events-auto h-[520px] max-h-[68vh] lg:h-[540px]"
                    >
                        <AskMe />
                    </motion.div>
                </div>
            </div>

            <Ticker />
        </section>
    );
};

export default Header;